<script setup lang="ts">
import { computed, ComputedRef, onMounted } from 'vue'
import { useInstanceStore } from '@/store/backendInstances.js'
import { useDisplay } from 'vuetify/lib/framework'
import ActivityChart from '@/composables/charts/quickCharts/ActivityChart.vue'
import SpeedChart from '@/composables/charts/quickCharts/GlobalSpeedChart.vue'
import GlobalGridScoreChart from '@/composables/charts/quickCharts/GlobalGridScoreChart.vue'
import GenericInfluxChart from '@/composables/charts/GenericInfluxChart.vue'
import { GeneralChartTimeSetup, timeSetupRegistry } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { ANNOTATOR_TYPES } from '@/composables/charts/influx-charts/ChartIncidentAnnotators'
import BarnEquipmentRegionSumChart from '@/composables/charts/quickCharts/BarnEquipmentRegionSumChart.vue'
import FrontBackRegionSumChart from '@/composables/charts/quickCharts/FrontBackRegionSumChart.vue'
import LeftRightRegionSumChart from '@/composables/charts/quickCharts/LeftRightRegionSumChart.vue'
import LabelCountRegionSumChart from '@/composables/charts/quickCharts/GlobalLabelCountRegionSumChart.vue'
import CameraCountRegionSumChart from '@/composables/charts/quickCharts/CameraCountRegionSumChart.vue'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import { CameraBasedInfluxLineChartBuilder } from '@/composables/charts/influx-charts/chart-builders/CameraBasedInfluxLineChartBuilder'
import { IRInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/IRChartBuilder'
import { RegionInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionChartBuilder'
import { RegionSumInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionSumChartBuilder'
import { useRouter } from 'vue-router'

const instanceStore = useInstanceStore()
const { mobile } = useDisplay()
const router = useRouter()
const canSeeLongPos = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.longPosition'])
})
const canSeeDistributionScores = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.distribution'])
})
const canSeeBucketScores = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.distribution'])
})
const canSeeRegions = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.regions'])
})
const canSeeActivity = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.activity'])
})
const canSeeSpeed = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.speed'])
})
const canSeeImageQuality = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.imageQuality'])
})

const canSeeRegionSums = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.regionSums'])
})

const providedStart: ComputedRef<number | undefined> = computed(() => {
  if (router.currentRoute.value.query.start) {
    const start = parseInt(router.currentRoute.value.query.start as string)
    if (!isNaN(start)) {
      return start
    }
  }
  return undefined
})
const providedEnd: ComputedRef<number | undefined> = computed(() => {
  if (router.currentRoute.value.query.end) {
    const end = parseInt(router.currentRoute.value.query.end as string)
    if (!isNaN(end)) {
      return end
    }
  }
  return undefined
})
onMounted(() => {
  if (providedStart.value || providedEnd.value) {
    timeSetupRegistry.updateSynchronizedTimeSetupsWithAbsoluteTime('measurementsDashboard', providedStart.value, providedEnd.value)
  }
})
</script>

<template>
  <v-container
    fluid
    class="py-4"
    :class="mobile ? 'px-4' : 'px-8'"
  >
    <v-row justify="center">
      <div class="px-4 py-2 w-100 d-flex flex-row justify-space-between align-end flex-wrap">
        <v-card-title>
          {{ $t('measurements.titles.barn_measurements') }}
        </v-card-title>
      </div>

      <v-col
        v-if="canSeeActivity"
        :cols="12"
      >
        <ActivityChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></ActivityChart>
      </v-col>

      <v-col
        v-if="canSeeSpeed"
        :cols="12"
      >
        <SpeedChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></SpeedChart>
      </v-col>

      <v-col
        v-if="canSeeBucketScores"
        :cols="12"
      >
        <GlobalGridScoreChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></GlobalGridScoreChart>
      </v-col>
      <v-col
        v-if="canSeeDistributionScores"
        :cols="12"
      >
        <GenericInfluxChart
          :chart-builder="
            new RegionSumInfluxChartBuilder({
              chartType: CHART_TYPES.REGION_SUMS,
              field: 'meanGridScore',
              timeSetup: GeneralChartTimeSetup.lastNDays(4, 'measurementsDashboard').serialize(),
              title: $t('charts.titles.gridScore'),
              subtitle: $t('charts.subtitles.gridScore'),
              incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
              incidentReducer: { reducerType: 'penState' },
              regionSumProperty: 'camera',
              defaultCollapsed: true,
              regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: true },
              xAggregation: 'mean',
            })
          "
        ></GenericInfluxChart>
      </v-col>

      <v-col
        v-if="canSeeLongPos"
        :cols="12"
      >
        <GenericInfluxChart
          :chart-builder="
            new CameraBasedInfluxLineChartBuilder({
              chartType: CHART_TYPES.CAMERA_BASED_LINE,
              measurement: 'longPosition',
              field: 'score',
              xAggregation: 'count',
              timeSetup: GeneralChartTimeSetup.lastNDays(4, 'measurementsDashboard', 1000 * 60).serialize(),
              title: $t('charts.titles.long_position_counts'),
              subtitle: $t('charts.subtitles.long_position_counts'),
              groupByTags: ['label'],
              fill: 0,
              defaultCollapsed: true,
              cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
            })
          "
        ></GenericInfluxChart>
      </v-col>
      <v-col
        v-if="canSeeLongPos"
        :cols="12"
      >
        <GenericInfluxChart
          :chart-builder="
            new CameraBasedInfluxLineChartBuilder({
              chartType: CHART_TYPES.CAMERA_BASED_LINE,
              measurement: 'longPosition',
              field: 'score',
              xAggregation: 'count',
              timeSetup: GeneralChartTimeSetup.lastNDays(4, 'measurementsDashboard', 1000 * 60).serialize(),
              title: $t('charts.titles.long_position_counts'),
              subtitle: $t('charts.subtitles.long_position_counts'),
              groupByTags: ['label'],
              fill: 0,
              defaultCollapsed: true,
              cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
            })
          "
        ></GenericInfluxChart>
      </v-col>

      <v-col
        v-if="canSeeRegionSums"
        :cols="12"
      >
        <BarnEquipmentRegionSumChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></BarnEquipmentRegionSumChart>
      </v-col>

      <v-col
        v-if="canSeeImageQuality"
        :cols="12"
      >
        <GenericInfluxChart
          :chart-builder="
            new IRInfluxChartBuilder({
              timeSetup: GeneralChartTimeSetup.lastNDays(4, 'measurementsDashboard', 1000 * 60 * 10).serialize(),
              chartType: CHART_TYPES.IR,
              defaultCollapsed: true,
              seriesBasedHeight: true,
              xAggregation: 'first',
              cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
              title: $t('charts.titles.nightvision_range_bar'),
            })
          "
        ></GenericInfluxChart>
      </v-col>

      <v-col
        v-if="canSeeActivity"
        :cols="12"
      >
        <GenericInfluxChart
          :chart-builder="
            new CameraBasedInfluxLineChartBuilder({
              chartType: CHART_TYPES.CAMERA_BASED_LINE,
              measurement: 'activity',
              field: 'activity',
              xAggregation: 'mean',
              timeSetup: GeneralChartTimeSetup.lastNDays(4, 'measurementsDashboard').serialize(),
              title: $t('charts.titles.activity'),
              subtitle: $t('charts.subtitles.activityPerCamera'),
              incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
              incidentReducer: { reducerType: 'penState' },
              cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
            })
          "
        ></GenericInfluxChart>
      </v-col>

      <v-col
        v-if="canSeeSpeed"
        :cols="12"
      >
        <GenericInfluxChart
          :chart-builder="
            new CameraBasedInfluxLineChartBuilder({
              chartType: CHART_TYPES.CAMERA_BASED_LINE,
              measurement: 'speed',
              field: 'speed',
              xAggregation: 'mean',
              timeSetup: GeneralChartTimeSetup.lastNDays(4, 'measurementsDashboard').serialize(),
              title: $t('charts.titles.speed'),
              subtitle: $t('charts.subtitles.speedPerCamera'),
              incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
              incidentReducer: { reducerType: 'penState' },
              cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
            })
          "
        ></GenericInfluxChart>
      </v-col>

      <v-col
        v-if="canSeeRegionSums"
        :cols="12"
      >
        <FrontBackRegionSumChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></FrontBackRegionSumChart>
      </v-col>

      <v-col
        v-if="canSeeRegionSums"
        :cols="12"
      >
        <LeftRightRegionSumChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></LeftRightRegionSumChart>
      </v-col>
      <v-col
        v-if="canSeeRegionSums"
        :cols="12"
      >
        <LabelCountRegionSumChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></LabelCountRegionSumChart>
      </v-col>

      <v-col
        v-if="canSeeRegionSums"
        :cols="12"
      >
        <CameraCountRegionSumChart
          synchronization-key="measurementsDashboard"
          :days="4"
        ></CameraCountRegionSumChart>
      </v-col>

      <v-col
        v-if="canSeeRegions && instanceStore.getSelectedInstanceSpecies === 'pigs'"
        :cols="12"
      >
        <GenericInfluxChart
          :chart-builder="
            new RegionInfluxChartBuilder({
              title: $t('charts.titles.regions'),
              timeSetup: GeneralChartTimeSetup.lastNDays(4, 'measurementsDashboard').serialize(),
              subtitle: $t('charts.subtitles.regionsPen'),
              chartType: CHART_TYPES.REGIONS,
              regionType: 'pen',
              cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
              labelTagSelector: {
                tag: 'label',
                allowEmptyValues: false,
                groupByValues: true,
                useAll: false,
                specificValues: [
                  'pig',
                  'pig_sitting',
                  'pig_standing',
                  'piglet',
                  'piglet_sitting',
                  'piglet_standing',
                  'sow_lying_belly',
                  'sow_lying_side',
                  'sow_sitting',
                  'sow_standing',
                ],
              },
              regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: true },
              xAggregation: 'mean',
              incidentReducer: { reducerType: 'penState' },
              incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
            })
          "
        ></GenericInfluxChart>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped></style>
